/* ============================================================
   Alice Lane — Insider Program, Meta ad-click fields
   Fills the Klaviyo form's fbp / fbc fields so Meta CAPI can
   match a signup back to the ad click that produced it:
     fbp — the Meta pixel's _fbp cookie, verbatim
     fbc — fb.1.{ms}.{fbclid}, built from ?fbclid on this URL
   Klaviyo's form editor has no hidden-field type, so both are
   ordinary text inputs bound to the fbp / fbc profile
   properties and hidden by insider-program.css — the same
   trick the Birthday and State rows already use.
   Loaded only where the section's "Populate Meta fbp/fbc
   fields" setting is on, i.e. the insider-ads template.
   ============================================================ */
(function () {
  'use strict';

  var RETRY_MS = 300;
  var RETRY_LIMIT = 34; /* ~10s — the pixel can write _fbp after the form renders */

  function readCookie(name) {
    var pairs = document.cookie ? document.cookie.split('; ') : [];
    for (var i = 0; i < pairs.length; i++) {
      var eq = pairs[i].indexOf('=');
      if (eq > -1 && pairs[i].slice(0, eq) === name) {
        return decodeURIComponent(pairs[i].slice(eq + 1));
      }
    }
    return '';
  }

  /* "fb.1" is Meta's version + subdomain index; the timestamp is when the
     value was built. Built once at load so the retries below don't each
     stamp a different time. Strictly from the URL — no fbclid, no value. */
  function buildFbc() {
    var match = window.location.search.match(/[?&]fbclid=([^&#]+)/);
    if (!match) return '';
    var fbclid = decodeURIComponent(match[1]);
    return fbclid ? 'fb.1.' + Date.now() + '.' + fbclid : '';
  }

  var FBC = buildFbc();

  /* Klaviyo renders the form with React, which discards a plain
     `input.value = x` on its next render. Writing through the native value
     setter and dispatching a bubbling "input" event is what makes React
     adopt the value, so it survives into the submitted payload. */
  function setValue(input, value) {
    if (input.value === value) return;
    var descriptor = Object.getOwnPropertyDescriptor(window.HTMLInputElement.prototype, 'value');
    if (descriptor && descriptor.set) {
      descriptor.set.call(input, value);
    } else {
      input.value = value;
    }
    input.dispatchEvent(new Event('input', { bubbles: true }));
    input.dispatchEvent(new Event('change', { bubbles: true }));
  }

  /* Klaviyo builds each input's id from the field's label — "Birthday_xxx",
     "State_xxx" — but the casing and suffix are its own business, so try the
     id prefix and name first, then sweep the inputs case-insensitively. */
  function findInput(form, key) {
    var input = form.querySelector('input[id^="' + key + '"], input[name="' + key + '"]');
    if (input) return input;
    var inputs = form.querySelectorAll('input');
    for (var i = 0; i < inputs.length; i++) {
      if ((inputs[i].id || '').toLowerCase().indexOf(key) === 0) return inputs[i];
    }
    return null;
  }

  /* These are real text inputs, not hidden ones: if insider-program.css were
     ever missing or late they would show as two stray empty boxes, so hide
     the row inline as well. */
  function hideRow(input) {
    var row = input.closest('[data-testid="form-row"]') || input.parentElement;
    if (row) row.style.display = 'none';
  }

  /* True once there is nothing left to wait for — either both fields are
     filled, or the value simply isn't available on this visit. */
  function populate(root) {
    var form = root.querySelector('.insider-signup--embed [class*="klaviyo-form-"]');
    if (!form) return false;

    var pairs = [['fbp', readCookie('_fbp')], ['fbc', FBC]];
    var settled = true;

    pairs.forEach(function (pair) {
      var input = findInput(form, pair[0]);
      if (!input) {
        settled = false; /* Klaviyo hasn't rendered the fields yet */
        return;
      }
      hideRow(input);
      if (pair[1]) {
        setValue(input, pair[1]);
      } else if (pair[0] === 'fbp') {
        settled = false; /* the pixel may still be writing the cookie */
      }
    });

    return settled;
  }

  function eachRoot(scope, fn) {
    (scope || document).querySelectorAll('.insider-signup--root').forEach(fn);
  }

  function watch(root) {
    if (root.dataset.insiderMetaBound) return;
    root.dataset.insiderMetaBound = '1';
    if (populate(root)) return;
    var tries = 0;
    var timer = window.setInterval(function () {
      if (populate(root) || ++tries >= RETRY_LIMIT) window.clearInterval(timer);
    }, RETRY_MS);
  }

  function refresh() {
    eachRoot(document, populate);
  }

  /* Klaviyo mounts the embed asynchronously and announces it here. The
     interval in watch() covers the case where this event fired first. */
  window.addEventListener('klaviyoForms', function (event) {
    var type = ((event && event.detail) || {}).type;
    if (type === 'embedOpen' || type === 'open' || type === 'stepSubmit') refresh();
  });

  /* Anyone touching the form is well ahead of the submit, and by then the
     _fbp cookie has certainly landed — a free second chance at it. */
  document.addEventListener('focusin', function (event) {
    var target = event.target;
    if (target && target.closest && target.closest('.insider-signup--embed')) refresh();
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { eachRoot(document, watch); });
  } else {
    eachRoot(document, watch);
  }

  document.addEventListener('shopify:section:load', function (event) {
    eachRoot(event.target, watch);
  });
})();
